import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pbAuthedFetch } from "../authHeaders";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const fmtFull = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const CATEGORY_META: Record<string, { icon: string; label: string; color: string }> = {
  'right-sizing':       { icon: '📐', label: 'Right-Sizing',        color: '#3b82f6' },
  'reserved-instances': { icon: '🔒', label: 'Reserved Instances',  color: '#8b5cf6' },
  'storage':            { icon: '💾', label: 'Storage',             color: '#06b6d4' },
  'idle-resources':     { icon: '⏸️', label: 'Idle Resources',      color: '#f59e0b' },
  'data-transfer':      { icon: '🌐', label: 'Data Transfer',       color: '#ec4899' },
  'lambda':             { icon: '⚡', label: 'Lambda',              color: '#10b981' },
};

const EFFORT_PILL: Record<string, string> = {
  low:    'pill--ok',
  medium: 'pill--warn',
  high:   'pill--risk',
};

interface Opportunity {
  id: string;
  category: string;
  title: string;
  description: string;
  current_cost: number;
  projected_cost: number;
  monthly_savings: number;
  confidence: number;
  effort: string;
  services: string[];
}

export function CostSavingsPage() {
  const { data: savingsData, isLoading: savingsLoading, error: savingsError, refetch: refetchSavings } = useQuery({
    queryKey: ['cost-savings'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/ai/cost-savings');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const { data: predictionsData } = useQuery({
    queryKey: ['ai-predictions'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/ai/predictions');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const savings = savingsData?.savings;
  const opportunitiesRaw: Opportunity[] = savings?.opportunities ?? [];
  const predictions = predictionsData?.predictions;

  // The 8 header buttons below (Category/Title/.../Effort) render as
  // clickable — `.th`, no `th--static` — but never had onClick wiring, so
  // clicking highlighted the active-sort styling without ever reordering
  // opportunitiesRaw.
  type SortKey = 'category' | 'title' | 'services' | 'current_cost' | 'projected_cost' | 'monthly_savings' | 'confidence' | 'effort';
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  };
  const sortValue = (o: Opportunity, key: SortKey): string | number =>
    key === 'services' ? o.services.join(', ') : o[key];
  const opportunities = sortKey == null
    ? opportunitiesRaw
    : [...opportunitiesRaw].sort((a, b) => {
        const av = sortValue(a, sortKey);
        const bv = sortValue(b, sortKey);
        const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortAsc ? cmp : -cmp;
      });
  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '');
  const ariaSortOf = (key: SortKey) => (sortKey !== key ? 'none' : sortAsc ? 'ascending' : 'descending');

  // Aggregate by category for chart
  const categoryBreakdown = Object.entries(
    opportunities.reduce<Record<string, number>>((acc, o) => {
      acc[o.category] = (acc[o.category] || 0) + o.monthly_savings;
      return acc;
    }, {})
  ).map(([key, value]) => ({
    name: CATEGORY_META[key]?.label ?? key,
    value: Math.round(value),
    color: CATEGORY_META[key]?.color ?? '#6b7280',
  }));

  const isLoading = savingsLoading;
  const hasError = savingsError;

  /* ---------- Loading ---------- */
  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">FinOps</p>
              <h1 className="pagehead__title">Cost Savings</h1>
              <div className="pagehead__facts">
                <span className="fact">AI-powered cost optimization</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading cost savings data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ---------- Error ---------- */
  if (hasError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">FinOps</p>
              <h1 className="pagehead__title">Cost Savings</h1>
              <div className="pagehead__facts">
                <span className="fact">AI-powered cost optimization</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load cost savings</h3>
                <p className="state__body">
                  Could not connect to the cost savings API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetchSavings()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ---------- Empty ---------- */
  if (!savings || opportunities.length === 0) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">FinOps</p>
              <h1 className="pagehead__title">Cost Savings</h1>
              <div className="pagehead__facts">
                <span className="fact">AI-powered cost optimization</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <h3 className="state__title">No savings opportunities found</h3>
                <p className="state__body">
                  The AI engine has not identified any cost optimization opportunities yet. Check back after more usage data has been collected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ---------- Main ---------- */
  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">FinOps</p>
            <h1 className="pagehead__title">Cost Savings</h1>
            <div className="pagehead__facts">
              <span className="fact">AI-powered cost optimization recommendations</span>
              <span className="fact">Updated: <b>live</b></span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Summary notice */}
        <div className="notice notice--info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <p className="notice__title">Savings Summary</p>
            <p className="notice__body">
              <strong>{fmt(savings.total_potential_monthly)}/mo</strong> potential savings across{' '}
              <strong>{opportunities.length} opportunities</strong>.
              Annual projection: <strong>{fmt(savings.total_potential_annual)}</strong> if all recommendations implemented.
            </p>
          </div>
        </div>

        {/* Summary metric indicators */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Cost savings summary">
          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Monthly</p>
              <p className="ind__full">Monthly Savings Potential</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{fmt(savings.total_potential_monthly)}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--ok)' }}></i>
              <b style={{ color: 'var(--ok)' }}>Potential</b> — across {opportunities.length} opportunities
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Annual</p>
              <p className="ind__full">Annual Projection</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{fmt(savings.total_potential_annual)}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--ok)' }}></i>
              <b style={{ color: 'var(--ok)' }}>Projected</b> — if all recommendations implemented
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Opps</p>
              <p className="ind__full">Opportunities Found</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{opportunities.length}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--gold)' }}>{opportunities.filter(o => o.effort === 'low').length} quick wins</b> — low-effort items
            </p>
          </div>
        </div>

        {/* Opportunities table */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Optimization</p>
              <h2 className="box__title">Savings Opportunities</h2>
            </div>
          </div>
          <div className="box__body box__body--flush">
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th><button className="th" onClick={() => toggleSort('category')} aria-sort={ariaSortOf('category')}>Category{sortIndicator('category')}</button></th>
                    <th><button className="th" onClick={() => toggleSort('title')} aria-sort={ariaSortOf('title')}>Title{sortIndicator('title')}</button></th>
                    <th><button className="th" onClick={() => toggleSort('services')} aria-sort={ariaSortOf('services')}>Services{sortIndicator('services')}</button></th>
                    <th><button className="th th--num" onClick={() => toggleSort('current_cost')} aria-sort={ariaSortOf('current_cost')}>Current Cost{sortIndicator('current_cost')}</button></th>
                    <th><button className="th th--num" onClick={() => toggleSort('projected_cost')} aria-sort={ariaSortOf('projected_cost')}>Projected Cost{sortIndicator('projected_cost')}</button></th>
                    <th><button className="th th--num" onClick={() => toggleSort('monthly_savings')} aria-sort={ariaSortOf('monthly_savings')}>Monthly Savings{sortIndicator('monthly_savings')}</button></th>
                    <th><button className="th" onClick={() => toggleSort('confidence')} aria-sort={ariaSortOf('confidence')}>Confidence{sortIndicator('confidence')}</button></th>
                    <th><button className="th" onClick={() => toggleSort('effort')} aria-sort={ariaSortOf('effort')}>Effort{sortIndicator('effort')}</button></th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((o) => {
                    const meta = CATEGORY_META[o.category] ?? { icon: '📦', label: o.category, color: '#6b7280' };
                    const effortPill = EFFORT_PILL[o.effort] ?? 'pill--warn';
                    return (
                      <tr key={o.id}>
                        <td>
                          <span className="pill" style={{
                            background: `${meta.color}18`,
                            borderColor: `${meta.color}44`,
                          }}>
                            <i style={{ background: meta.color }}></i>
                            {meta.label}
                          </span>
                        </td>
                        <td className="td--name" title={o.description} style={{ cursor: 'help' }}>{o.title}</td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)' }}>
                            {o.services.map((s) => (
                              <span key={s} className="pill" style={{ padding: 'var(--sp-1) var(--sp-2)', fontSize: 'var(--text-xs)' }}>
                                {s}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="td--num" style={{ textDecoration: 'line-through', color: 'var(--ink-45)' }}>
                          {o.current_cost > 0 ? fmtFull(o.current_cost) : '—'}
                        </td>
                        <td className="td--num">
                          {o.projected_cost > 0 ? fmtFull(o.projected_cost) : '—'}
                        </td>
                        <td className="td--num" style={{ color: 'var(--ok)', fontWeight: 600 }}>
                          +{fmtFull(o.monthly_savings)}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                            <div style={{ width: 60, height: 6, borderRadius: 3, background: 'var(--hair)', overflow: 'hidden' }}>
                              <div
                                style={{
                                  width: `${o.confidence * 100}%`,
                                  height: '100%',
                                  borderRadius: 3,
                                  background: o.confidence >= 0.9 ? 'var(--ok)' : o.confidence >= 0.75 ? 'var(--gold)' : 'var(--risk)',
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{Math.round(o.confidence * 100)}%</span>
                          </div>
                        </td>
                        <td>
                          <span className={`pill ${effortPill}`}>
                            <i></i>
                            {o.effort}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Charts row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-5)' }}>
          {/* Pie chart */}
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Breakdown</p>
                <h2 className="box__title">Savings by Category</h2>
              </div>
            </div>
            <div className="box__body">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 24 }}>
                  <Pie
                    data={categoryBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${fmt(value)}`}
                  >
                    {categoryBreakdown.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => fmt(Number(v))} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bar chart */}
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Comparison</p>
                <h2 className="box__title">Monthly Savings by Opportunity</h2>
              </div>
            </div>
            <div className="box__body">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={opportunities.map(o => ({ name: o.title.length > 20 ? o.title.slice(0, 20) + '…' : o.title, savings: o.monthly_savings }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--ink-45)' }} />
                  <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11, fill: 'var(--ink-45)' }} />
                  <Tooltip formatter={(v: any) => fmtFull(Number(v))} />
                  <Bar dataKey="savings" fill="var(--ok)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* AI Insights */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Intelligence</p>
              <h2 className="box__title">AI Insights</h2>
            </div>
          </div>
          <div className="box__body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
              {predictions?.cost_forecast && (
                <div className="box" style={{ background: 'var(--surface-sub)' }}>
                  <div className="box__body">
                    <p style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', color: 'var(--ink-45)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 'var(--sp-2)' }}>
                      30-Day Cost Forecast
                    </p>
                    <p style={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
                      {fmt(predictions.cost_forecast.next_30_days)}
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', marginLeft: 'var(--sp-2)' }}>
                        ({Math.round(predictions.cost_forecast.confidence * 100)}% confidence)
                      </span>
                    </p>
                    <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--sp-2)', color: predictions.cost_forecast.trend === 'increasing' ? 'var(--gold)' : 'var(--ok)' }}>
                      Trend: {predictions.cost_forecast.trend}
                    </p>
                    {predictions.cost_forecast.drivers?.length > 0 && (
                      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', marginTop: 'var(--sp-1)' }}>
                        Drivers: {predictions.cost_forecast.drivers.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {predictions?.anomaly_detections?.length > 0 && (
                <div className="box" style={{ background: 'var(--surface-sub)' }}>
                  <div className="box__body">
                    <p style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', color: 'var(--ink-45)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 'var(--sp-2)' }}>
                      Anomaly Detections
                    </p>
                    {predictions.anomaly_detections.map((a: { type: string; service: string; severity: string; detail: string }, i: number) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--sp-1) 0', borderBottom: i < predictions.anomaly_detections.length - 1 ? '1px solid var(--hair)' : 'none' }}>
                        <span>
                          <span className={`pill ${a.severity === 'high' ? 'pill--risk' : 'pill--warn'}`} style={{ marginRight: 'var(--sp-2)' }}>
                            <i></i>
                            {a.severity}
                          </span>
                          {a.detail}
                        </span>
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>{a.service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Key recommendations */}
            {opportunities.filter(o => o.confidence >= 0.85).length > 0 && (
              <div className="notice notice--ok" style={{ marginTop: 'var(--sp-4)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                <div>
                  <p className="notice__title">Key Recommendations</p>
                  <ul style={{ margin: 'var(--sp-2) 0 0', paddingLeft: 'var(--sp-5)', lineHeight: 1.8 }}>
                    {opportunities
                      .filter(o => o.confidence >= 0.85)
                      .sort((a, b) => b.monthly_savings - a.monthly_savings)
                      .slice(0, 4)
                      .map(o => (
                        <li key={o.id}>
                          <strong>{o.title}</strong> — save {fmtFull(o.monthly_savings)}/mo ({Math.round(o.confidence * 100)}% confidence, {o.effort} effort)
                        </li>
                      ))
                    }
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
